import React, { useCallback, useEffect, useRef, useState } from "react";
import { AutoComplete, Typography } from "antd";
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  FolderOutlined,
  FormOutlined,
  InboxOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getSupabaseClient } from "../../core/supabase/client.js";
import { useAppState } from "../../core/app_state/AppStateContext.js";
import { useIsMobile } from "../../core/hooks/useIsMobile";

const { Text } = Typography;

type ResultType =
  | "project"
  | "member"
  | "location"
  | "session"
  | "asset"
  | "formx";

interface SearchResult {
  id: string;
  type: ResultType;
  title: string;
  subtitle?: string;
  href: string;
}

const TYPE_META: Record<
  ResultType,
  { label: string; icon: React.ReactNode; color: string }
> = {
  project: { label: "Dự án", icon: <FolderOutlined />, color: "var(--accent)" },
  member: { label: "Thành viên", icon: <TeamOutlined />, color: "var(--blue)" },
  location: {
    label: "Địa điểm",
    icon: <EnvironmentOutlined />,
    color: "var(--green)",
  },
  session: {
    label: "Lịch làm việc",
    icon: <ClockCircleOutlined />,
    color: "var(--amber)",
  },
  asset: { label: "Sản phẩm", icon: <InboxOutlined />, color: "var(--purple)" },
  formx: { label: "Mẫu biểu", icon: <FormOutlined />, color: "var(--purple)" },
};

const TYPE_ORDER: ResultType[] = [
  "project",
  "member",
  "location",
  "session",
  "asset",
  "formx",
];

function fmtVN(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

async function runSearch(
  orgId: string,
  query: string,
): Promise<SearchResult[]> {
  const supabase = getSupabaseClient() as any;

  const [
    projectsRes,
    membersRes,
    locationsRes,
    sessionsRes,
    assetsRes,
    formsRes,
  ] = await Promise.allSettled([
    supabase.rpc("search_projects", {
      p_org_id: orgId,
      search_query: query,
      row_limit: 5,
    }),
    supabase.rpc("search_profiles_of_org", {
      p_org_id: orgId,
      search_query: query,
      row_limit: 5,
    }),
    supabase.rpc("search_locations_by_scope", {
      p_org_id: orgId,
      search_query: query,
      row_limit: 5,
      p_scope: "any",
    }),
    supabase.rpc("search_sessions", {
      p_org_id: orgId,
      search_query: query,
      row_limit: 5,
    }),
    supabase
      .from("stock_assets")
      .select("id, name, sku, project_id")
      .eq("org_id", orgId)
      .ilike("name", `%${query}%`)
      .limit(5),
    supabase.rpc("search_formx_forms", {
      p_org_id: orgId,
      search_query: query,
      row_limit: 5,
    }),
  ]);

  const results: SearchResult[] = [];

  if (projectsRes.status === "fulfilled") {
    for (const r of projectsRes.value.data ?? []) {
      results.push({
        id: r.id,
        type: "project",
        title: r.name,
        subtitle: r.description ?? undefined,
        href: `/projects/${r.id}`,
      });
    }
  }

  if (membersRes.status === "fulfilled") {
    for (const r of membersRes.value.data ?? []) {
      const pd = r.profile_data ?? {};
      const fullName = [pd.first_name, pd.last_name].filter(Boolean).join(" ");
      results.push({
        id: r.id,
        type: "member",
        title: pd.email ?? r.profile_id,
        subtitle: fullName || (r.role_data?.name ?? undefined),
        href: `/users/${r.profile_id}`,
      });
    }
  }

  if (locationsRes.status === "fulfilled") {
    for (const r of locationsRes.value.data ?? []) {
      results.push({
        id: r.id,
        type: "location",
        title: r.name,
        subtitle: r.full_address_cache || r.address_line,
        href: `/locations/${r.id}`,
      });
    }
  }

  if (sessionsRes.status === "fulfilled") {
    for (const r of sessionsRes.value.data ?? []) {
      const status = r.ended_at
        ? "Đã xong"
        : r.started_at
          ? "Đang chạy"
          : "Đã lên lịch";
      const date = fmtVN(r.scheduled_start_at ?? r.started_at ?? r.created_at);
      results.push({
        id: r.id,
        type: "session",
        title: r.location?.name ?? `Lịch ${r.id.slice(0, 8)}`,
        subtitle: `${status}${date ? " · " + date : ""}`,
        href: `/sessions?drawer=${r.id}`,
      });
    }
  }

  if (assetsRes.status === "fulfilled") {
    for (const r of assetsRes.value.data ?? []) {
      results.push({
        id: r.id,
        type: "asset",
        title: r.name,
        subtitle: r.sku,
        href: `/stock?drawer=${r.id}`,
      });
    }
  }

  if (formsRes.status === "fulfilled") {
    for (const r of formsRes.value.data ?? []) {
      results.push({
        id: r.id,
        type: "formx",
        title: r.name,
        subtitle: r.description ?? undefined,
        href: `/formx/${r.id}`,
      });
    }
  }

  return results;
}

function buildOptions(results: SearchResult[]) {
  const byType: Partial<Record<ResultType, SearchResult[]>> = {};
  for (const r of results) {
    (byType[r.type] ??= []).push(r);
  }

  const groups: any[] = [];
  for (const type of TYPE_ORDER) {
    const items = byType[type];
    if (!items?.length) continue;
    const meta = TYPE_META[type];
    groups.push({
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: meta.color,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            padding: "6px 0 2px",
          }}
        >
          {meta.icon}
          {meta.label}
          <span
            style={{
              color: "var(--text3)",
              fontWeight: 400,
              textTransform: "none",
            }}
          >
            ({items.length})
          </span>
        </div>
      ),
      options: items.map((item) => ({
        value: `${item.href}||${item.id}`,
        label: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              padding: "2px 0",
            }}
          >
            <Text
              style={{ color: "var(--text)", fontSize: 13, lineHeight: "18px" }}
            >
              {item.title}
            </Text>
            {item.subtitle && (
              <Text
                style={{
                  color: "var(--text2)",
                  fontSize: 11,
                  lineHeight: "15px",
                }}
              >
                {item.subtitle}
              </Text>
            )}
          </div>
        ),
      })),
    });
  }

  return groups;
}

const EMPTY_OPTION = [
  {
    label: null,
    options: [
      {
        value: "__empty__",
        disabled: true,
        label: (
          <Text style={{ color: "var(--text3)", fontSize: 13 }}>
            Không tìm thấy kết quả
          </Text>
        ),
      },
    ],
  },
];

export function GlobalSearch() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { activeOrg } = useAppState();
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearch = useCallback(
    async (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!value || value.trim().length < 3 || !activeOrg?.id) {
        setOptions([]);
        return;
      }

      const orgId = activeOrg.id;
      debounceRef.current = setTimeout(async () => {
        setSearching(true);
        try {
          const results = await runSearch(orgId, value.trim());
          setOptions(results.length ? buildOptions(results) : EMPTY_OPTION);
        } catch {
          setOptions(EMPTY_OPTION);
        } finally {
          setSearching(false);
        }
      }, 300);
    },
    [activeOrg],
  );

  const handleSelect = (value: string) => {
    if (value === "__empty__") return;
    const href = value.split("||")[0];
    if (!href) return;
    setQuery("");
    setOptions([]);
    navigate(href);
  };

  const handleClear = () => {
    setQuery("");
    setOptions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  return (
    <AutoComplete
      value={query}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={(value) => {
        if (!value) handleClear();
      }}
      popupMatchSelectWidth={420}
      getPopupContainer={() => document.body}
      classNames={{ popup: { root: isMobile ? "global-search-popup-mobile" : "" } }}
      styles={{
        popup: {
          root: {
            background: "var(--bg2)",
            border: "1px solid var(--border)",
            borderRadius: 8,
          },
        },
      }}
      style={{ width: 260, height: 32 }}
      prefix={
        <SearchOutlined
          style={{
            color: searching ? "var(--accent)" : "var(--text3)",
            transition: "color 0.2s",
          }}
        />
      }
      placeholder="Tìm kiếm..."
      allowClear
    />
  );
}
